import { Assets, Spritesheet, Texture } from 'pixi.js';
import { sound } from '../audio/sound';

interface Manifest {
  atlases: string[];
  backgrounds: Record<string, string>;
  sounds?: Record<string, string>;
}

const BASE = `${import.meta.env.BASE_URL}generated/`;
const frames = new Map<string, Texture>();
const backgrounds = new Map<string, Texture>();

/** Loads the generated atlas pages and background images (see tools/build-atlas.mjs). */
export async function loadAssets(onProgress?: (p: number) => void): Promise<void> {
  const manifest: Manifest = await (await fetch(`${BASE}manifest.json`)).json();
  sound.setFiles(Object.fromEntries(Object.entries(manifest.sounds ?? {}).map(([n, f]) => [n, BASE + f])));
  const sheetUrls = manifest.atlases.map((f) => BASE + f);
  const bgEntries = Object.entries(manifest.backgrounds);
  const total = sheetUrls.length + bgEntries.length;
  let done = 0;
  const tick = () => onProgress?.(++done / total);

  await Promise.all([
    ...sheetUrls.map(async (url) => {
      const sheet = await Assets.load<Spritesheet>(url);
      for (const [name, tex] of Object.entries(sheet.textures)) frames.set(name, tex);
      tick();
    }),
    ...bgEntries.map(async ([name, file]) => {
      backgrounds.set(name, await Assets.load<Texture>(BASE + file));
      tick();
    }),
  ]);
}

/** Texture for an atlas frame. Missing art shows a visible placeholder instead of crashing. */
export function frame(name: string): Texture {
  const tex = frames.get(name);
  if (!tex) {
    console.warn(`missing sprite: ${name}`);
    return Texture.WHITE;
  }
  return tex;
}

export function background(name: string): Texture {
  return backgrounds.get(name) ?? Texture.WHITE;
}

export function allFrameNames(): string[] {
  return [...frames.keys()].sort();
}
